import { Controller } from "@hotwired/stimulus"

// The campaign tags chip input (docs/CAMPAIGN_TAGS_PLAN.md): chips plus a typing box inside one
// form-input, over a plain comma-separated text field that is the value the form submits and the
// whole input without JavaScript. Enter, Tab, comma or space ends a tag; Backspace on an empty
// box removes the last chip; a pasted list splits the same way. Input is refused as typed:
// uppercase is lowercased, a character outside Campaign::TAG is dropped with a shake, the 25th
// character is ignored, the 11th tag is refused with the help text turning red. The panel
// suggests the account's recent tags (the suggestions value) containing what is typed, plus
// "Create" for a new one, while something is typed. The server normalises and validates again.
const DISALLOWED = /[^a-z0-9._:=-]/g
const SEPARATORS = /[,\s]+/

export default class extends Controller {
  static targets = ["field", "chips", "input", "hidden", "panel", "help"]
  static values = { suggestions: Array, max: { type: Number, default: 10 }, maxLength: { type: Number, default: 24 } }

  connect() {
    this.tags = this.hiddenTarget.value.split(SEPARATORS).map((t) => this.clean(t)).filter(Boolean)
    this.hiddenTarget.hidden = true          // from here on the plain field only carries the value
    this.hiddenTarget.tabIndex = -1
    this.inputTarget.hidden = false
    this.chipsTarget.hidden = false
    this.active = -1
    this.items = []
    this.onDocumentClick = (e) => { if (!this.element.contains(e.target)) this.hidePanel() }
    document.addEventListener("click", this.onDocumentClick, true)
    this.form = this.element.closest("form")
    this.onSubmit = () => this.commit()
    this.form?.addEventListener("submit", this.onSubmit)
    this.render()
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick, true)
    this.form?.removeEventListener("submit", this.onSubmit)
  }

  focus(e) { if (!e.target.closest(".tag-x")) this.inputTarget.focus() }

  // Typed text is normalised in place; anything dropped shakes the field.
  input() {
    const raw = this.inputTarget.value
    const clean = this.clean(raw)
    if (clean !== raw) {
      this.inputTarget.value = clean
      if (raw.toLowerCase() !== clean) this.shake()
    }
    this.showPanel()
  }

  keydown(e) {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "," || e.key === " ") {
      if (e.key === "Tab" && !this.inputTarget.value && this.active < 0) return   // an empty box lets Tab move on
      e.preventDefault()
      if (this.active >= 0 && this.items[this.active] !== undefined) this.add(this.items[this.active])
      else this.commit()
    } else if (e.key === "Backspace" && !this.inputTarget.value && this.tags.length) {
      this.removeTag(this.tags[this.tags.length - 1])
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!this.items.length) return
      e.preventDefault()
      this.active = (this.active + (e.key === "ArrowDown" ? 1 : -1) + this.items.length) % this.items.length
      this.paintPanel()
    } else if (e.key === "Escape") {
      this.hidePanel()
    }
  }

  paste(e) {
    const text = e.clipboardData?.getData("text")
    if (!text) return
    e.preventDefault()
    text.split(SEPARATORS).forEach((t) => this.add(t))
  }

  // Leaving the box keeps what was typed (a tag without Enter is not lost on submit).
  blur() {
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) { this.commit(); this.hidePanel() }
    }, 120)
  }

  commit() { if (this.inputTarget.value) this.add(this.inputTarget.value) }

  add(raw) {
    const t = this.clean(raw)
    this.inputTarget.value = ""
    if (!t) return this.render()
    if (this.tags.includes(t)) return this.render()
    if (this.tags.length >= this.maxValue) return this.refuse()
    this.tags.push(t)
    this.render()
  }

  remove(e) {
    const chip = e.target.closest("[data-tag]")
    if (chip) this.removeTag(chip.dataset.tag)
    this.inputTarget.focus()
  }

  removeTag(t) {
    this.tags = this.tags.filter((x) => x !== t)
    this.render()
  }

  clean(raw) {
    return raw.toLowerCase().replace(DISALLOWED, "").replace(/^[^a-z0-9]+/, "").slice(0, this.maxLengthValue)
  }

  render() {
    this.hiddenTarget.value = this.tags.join(",")
    this.chipsTarget.replaceChildren(...this.tags.map((t) => this.chip(t)))
    this.inputTarget.placeholder = this.tags.length ? "" : "Add a tag…"
    if (this.hasHelpTarget) this.helpTarget.classList.remove("text-danger")
    this.showPanel()
  }

  chip(t) {
    const chip = document.createElement("span")
    chip.className = "tag"
    chip.dataset.tag = t
    chip.textContent = t
    const x = document.createElement("button")
    x.type = "button"
    x.className = "tag-x"
    x.dataset.action = "tag-input#remove"
    x.setAttribute("aria-label", `Remove ${t}`)
    x.textContent = "×"
    chip.appendChild(x)
    return chip
  }

  // Only while something is typed: an always-open panel would sit over whatever is under the
  // field (the editor's Save button) and swallow the click.
  showPanel() {
    if (document.activeElement !== this.inputTarget) return this.hidePanel()
    const q = this.inputTarget.value
    if (!q) return this.hidePanel()
    const pool = this.suggestionsValue.filter((s) => !this.tags.includes(s))
    this.items = (q ? pool.filter((s) => s.includes(q)) : pool).slice(0, 8)
    const create = q && !this.tags.includes(q) && !this.items.includes(q)
    if (create) this.items.push(q)
    this.createIndex = create ? this.items.length - 1 : -1
    if (!this.items.length) return this.hidePanel()
    if (this.active >= this.items.length) this.active = -1
    this.paintPanel()
    this.panelTarget.hidden = false
  }

  paintPanel() {
    this.panelTarget.replaceChildren(...this.items.map((s, i) => {
      const row = document.createElement("div")
      row.className = "tag-suggest-item" + (i === this.active ? " is-active" : "") + (i === this.createIndex ? " tag-suggest-new" : "")
      if (i === this.createIndex) {
        row.append(`Create “${s}”`)
        const kbd = document.createElement("kbd")
        kbd.textContent = "Enter"
        row.appendChild(kbd)
      } else {
        const chip = document.createElement("span")
        chip.className = "tag"
        chip.textContent = s
        row.appendChild(chip)
      }
      // mousedown, not click: the box's blur would close the panel before a click lands
      row.addEventListener("mousedown", (e) => { e.preventDefault(); this.add(s); this.inputTarget.focus() })
      return row
    }))
  }

  hidePanel() {
    this.panelTarget.hidden = true
    this.active = -1
    this.items = []
  }

  shake() {
    this.fieldTarget.classList.remove("is-shaking")
    void this.fieldTarget.offsetWidth
    this.fieldTarget.classList.add("is-shaking")
  }

  refuse() {
    this.shake()
    if (this.hasHelpTarget) this.helpTarget.classList.add("text-danger")
  }
}
